'use es6';

import { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import ContentLockedPopover from 'ContentEditorUI/components/header/ContentLockedPopover';
import UIButton from 'UIComponents/button/UIButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UITooltipContent from 'UIComponents/tooltip/UITooltipContent';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import EPSaveButton from 'editor-components/header/EPSaveButton';
import { SaveStates } from 'editor-components/EPConstants';
import { isLastSaveToday } from 'editor-components/utils/durationUtils';
import { routeToPage } from 'ContentEditorUI/redux/actions/routingActions';
import { useDispatch } from 'react-redux';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TooltipContent = styled(UITooltipContent).withConfig({
  displayName: "BaymaxSaveButton__TooltipContent",
  componentId: "sc-6i4by2-0"
})(["font-size:14px;"]);
const BaymaxSaveButton = ({
  allowedSave,
  allowedSaveDisabledTooltip,
  contentTypeNoun,
  isDisabled,
  isUserJitad,
  isUngatedForPatagonia,
  onSaveClick,
  permanentlyDisabled,
  lastSave,
  saveState,
  shouldShowRevisions = true,
  onRevisionsClick
}) => {
  const dispatch = useDispatch();
  const handleOnRevisionsClick = useCallback(() => {
    if (onRevisionsClick) {
      onRevisionsClick();
    } else {
      dispatch(routeToPage('revisions'));
    }
  }, [dispatch, onRevisionsClick]);
  const handleTooltipOpen = useCallback(evt => {
    if (evt.target.value && !isUserJitad) {
      UsageTracker.trackEditorInteraction({
        action: 'Approvals Action',
        event: 'viewContentEditorAccessRequestTooltip'
      });
    }
  }, [isUserJitad]);
  function renderTooltipTimestamp() {
    if (!lastSave) return null;
    const lastSaveToday = isLastSaveToday({
      lastSave,
      now: I18n.moment.portalTz().valueOf()
    });

    // include date if user has not saved today
    const format = lastSaveToday ? 'LT' : 'l LT';
    const lastSavedTimeDisplay = I18n.moment.portalTz(lastSave).format(format);
    return /*#__PURE__*/_jsx("div", {
      className: "m-y-2",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        "data-test-save-type": "lastSaved",
        "data-test-id": "last-saved-timestamp",
        message: "header.save.lastSaved",
        options: {
          time: lastSavedTimeDisplay
        }
      })
    });
  }
  function renderTooltipContent() {
    if (!allowedSave) {
      return /*#__PURE__*/_jsx(UITooltipContent, {
        children: /*#__PURE__*/_jsx("div", {
          className: "m-y-2",
          children: allowedSaveDisabledTooltip || /*#__PURE__*/_jsx(FormattedMessage, {
            message: "components.header.saveButton.notAllowed",
            options: {
              noun: contentTypeNoun
            }
          })
        })
      });
    } else if (permanentlyDisabled) {
      return /*#__PURE__*/_jsx(UITooltipContent, {
        className: "save-disabled-drop",
        children: /*#__PURE__*/_jsx("div", {
          className: "m-y-2",
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "components.header.saveDisabledDrop.save.message"
          })
        })
      });
    }
    return /*#__PURE__*/_jsxs(TooltipContent, {
      children: [renderTooltipTimestamp(), /*#__PURE__*/_jsx("div", {
        className: "m-y-2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "header.save.autosaveMessage"
        })
      }), shouldShowRevisions && /*#__PURE__*/_jsx("div", {
        className: "m-y-2",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: 'header.save.versionHistoryText',
          options: {
            versionHistoryLink: /*#__PURE__*/_jsx(UIButton, {
              use: "link",
              disabled: !allowedSave,
              onClick: handleOnRevisionsClick,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: 'header.save.versionHistoryLink'
              })
            })
          }
        })
      })]
    });
  }
  if (isUngatedForPatagonia) {
    return /*#__PURE__*/_jsx(EPSaveButton, {
      isError: saveState === SaveStates.SAVE_FAILED,
      isSaved: saveState === SaveStates.SAVED_CHANGES,
      isSaving: saveState === SaveStates.SAVING_CHANGES,
      lastSave: lastSave,
      onClick: onSaveClick,
      renderTooltipContent: renderTooltipContent,
      saveState: saveState,
      disabled: !allowedSave || isDisabled || permanentlyDisabled,
      tooltipUse: "longform"
    });
  }
  const button = /*#__PURE__*/_jsx(UIButton, {
    onClick: onSaveClick,
    use: "secondary-ghost",
    size: "small",
    disabled: !allowedSave || isDisabled || permanentlyDisabled,
    "data-test-id": "save-button",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "components.header.saveButton.save"
    })
  });
  if (permanentlyDisabled) {
    return /*#__PURE__*/_jsx(ContentLockedPopover, {
      buttonType: 'save',
      placement: 'bottom right',
      children: button
    });
  } else if (!allowedSave) {
    return /*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "components.header.saveButton.notAllowed",
        options: {
          noun: contentTypeNoun
        }
      }),
      onOpenChange: handleTooltipOpen,
      children: button
    });
  }
  return button;
};
BaymaxSaveButton.propTypes = {
  allowedSave: PropTypes.bool.isRequired,
  allowedSaveDisabledTooltip: PropTypes.string,
  contentTypeNoun: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  onSaveClick: PropTypes.func.isRequired,
  permanentlyDisabled: PropTypes.bool.isRequired,
  isUserJitad: PropTypes.bool.isRequired,
  isUngatedForPatagonia: PropTypes.bool.isRequired,
  lastSave: PropTypes.instanceOf(Date),
  saveState: PropTypes.string.isRequired,
  shouldShowRevisions: PropTypes.bool,
  onRevisionsClick: PropTypes.func
};
export default BaymaxSaveButton;